import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

const Slider = () => {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const videoRef = useRef(null);
  const timeoutRef = useRef(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [playBlocked, setPlayBlocked] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/slides.json`)
      .then((res) => res.json())
      .then((data) => setSlides(data))
      .catch((err) => console.error('Failed to load slides:', err));
  }, []);

  // Filter valid slides
  const validSlides = slides.filter(slide => {
    if (slide.type === 'image' && slide.src) return true;
    if (slide.type === 'video' && slide.src) return true;
    if (slide.type === 'content' && slide.title) return true;
    return false;
  });

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % validSlides.length);
    setIsVideoPlaying(false);
    setPlayBlocked(false);
  };

  const handleVideoEnd = () => {
    setTimeout(nextSlide, 5000);
  };

  useEffect(() => {
    if (videoRef.current && validSlides[currentSlide]?.type === 'video') {
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            setIsVideoPlaying(true);
            setPlayBlocked(false);
          })
          .catch(() => {
            setIsVideoPlaying(false);
            setPlayBlocked(true);
          });
      }
    } else {
      setIsVideoPlaying(false);
      setPlayBlocked(false);
    }
  }, [currentSlide, validSlides]);

  useEffect(() => {
    if (validSlides[currentSlide]?.type === 'video' && videoRef.current) {
      videoRef.current.onended = handleVideoEnd;
    }
  }, [currentSlide, validSlides]);

  // Auto slide
  useEffect(() => {
    if (!isPaused && validSlides[currentSlide]?.type !== 'video') {
      timeoutRef.current = setTimeout(nextSlide, 3000);
    }
    return () => timeoutRef.current && clearTimeout(timeoutRef.current);
  }, [currentSlide, validSlides, isPaused]);

  const handleManualPlay = () => {
    if (videoRef.current) {
      videoRef.current.play()
        .then(() => {
          setIsVideoPlaying(true);
          setPlayBlocked(false);
        })
        .catch(() => {});
    }
  };

  if (validSlides.length === 0) return null;

  return (
    <div
      className="relative w-full max-w-6xl h-[400px] overflow-hidden mx-auto mt-8"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="relative w-full h-full">
        {validSlides.map((slide, index) => (
          <div
            key={index}
            className="absolute top-0 left-0 w-full h-full transition-all duration-500 ease-in-out"
            style={{
              transform: `translateX(${(index - currentSlide) * 100}%)`,
              opacity: index === currentSlide ? 1 : 0,
              pointerEvents: index === currentSlide ? 'auto' : 'none',
            }}
          >
            {slide.type === 'image' && (
              <img
                src={slide.src}
                alt={`Slide ${index + 1}`}
                className="w-full h-full object-contain"
              />
            )}

            {slide.type === 'video' && (
              <div className="relative w-full h-full">
                <video
                  ref={videoRef}
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  src={slide.src}
                />
                {!isVideoPlaying && playBlocked && (
                  <button
                    onClick={handleManualPlay}
                    className="absolute inset-0 m-auto w-16 h-16 bg-orange-500 bg-opacity-80 rounded-full flex items-center justify-center text-white text-2xl font-bold hover:bg-orange-600 transition"
                    aria-label="Play Video"
                  >
                    ▶
                  </button>
                )}
              </div>
            )}

            {slide.type === 'content' && (
              <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-100 to-white text-center p-8">
                {slide.image && (
                  <img src={slide.image} alt="Banner" className="w-32 h-auto mb-4" />
                )}
                <h2 className="text-3xl font-bold mb-2">{slide.title}</h2>
                <h3 className="text-lg text-gray-600 mb-4">{slide.subtitle}</h3>
                <p className="text-md text-gray-500 mb-4 max-w-xl">{slide.description}</p>

                {slide.buttonText && (
                  slide.buttonLink ? (
                    <Link
                      to={slide.buttonLink}
                      className="bg-orange-500 text-white px-6 py-2 rounded-full font-semibold hover:bg-orange-600 transition"
                    >
                      {slide.buttonText}
                    </Link>
                  ) : slide.externalLink ? (
                    <a
                      href={slide.externalLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-orange-500 text-white px-6 py-2 rounded-full font-semibold hover:bg-orange-600 transition"
                    >
                      {slide.buttonText}
                    </a>
                  ) : null
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination Dots */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
        {validSlides.map((_, index) => (
          <div
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full cursor-pointer ${
              currentSlide === index ? 'bg-orange-500' : 'bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default Slider;
